import apiClient from "@/lib/axios";
import {
  Property,
  PropertySearchParams,
  PropertySearchResponse,
  PropertyCreateData,
  PropertyUpdateData,
} from "@/services/types/property";

export const propertiesApi = {
  // Get all properties with filters and pagination
  getProperties: async (
    params: PropertySearchParams = {}
  ): Promise<PropertySearchResponse> => {
    // Flatten filters object to query params
    const queryParams: any = { ...params };
    if (params.filters) {
      // Spread filters into queryParams
      Object.assign(queryParams, params.filters);
      delete queryParams.filters;
    }

    const response = await apiClient.get("/api/properties", {
      params: queryParams,
    });
    // Backend returns { success, data, pagination }, frontend expects { properties, page, limit, total, totalPages }
    // Also map _id to id for frontend compatibility
    const properties = response.data.data.map((property: any) => ({
      ...property,
      id: property._id,
    }));
    return {
      properties,
      page: response.data.pagination.page,
      limit: response.data.pagination.limit,
      total: response.data.pagination.totalResults,
      totalPages: response.data.pagination.totalPages,
    };
  },

  // Get property by ID
  getPropertyById: async (id: string): Promise<Property> => {
    const response = await apiClient.get(`/api/properties/${id}`);
    // Backend returns { success, data }, frontend expects Property directly
    // Also map _id to id for frontend compatibility
    return {
      ...response.data.data,
      id: response.data.data._id,
    };
  },

  // Search properties
  searchProperties: async (
    params: PropertySearchParams
  ): Promise<PropertySearchResponse> => {
    const response = await apiClient.get("/api/properties/search", { params });
    // Backend returns { success, data, pagination }, frontend expects { properties, page, limit, total, totalPages }
    // Also map _id to id for frontend compatibility
    const properties = response.data.data.map((property: any) => ({
      ...property,
      id: property._id,
    }));
    return {
      properties,
      page: response.data.pagination.page,
      limit: response.data.pagination.limit,
      total: response.data.pagination.totalResults,
      totalPages: response.data.pagination.totalPages,
    };
  },

  // Get featured properties (not implemented in backend yet)
  getFeaturedProperties: async (): Promise<Property[]> => {
    const response = await apiClient.get("/api/properties/featured");
    return response.data;
  },

  // Get properties by agent (not implemented in backend yet)
  getPropertiesByAgent: async (
    agentId: string,
    params: PropertySearchParams = {}
  ): Promise<PropertySearchResponse> => {
    const response = await apiClient.get(`/api/properties/agent/${agentId}`, {
      params,
    });
    return response.data;
  },

  // Get similar properties (not implemented in backend yet)
  getSimilarProperties: async (
    propertyId: string,
    limit: number = 4
  ): Promise<Property[]> => {
    const response = await apiClient.get(
      `/api/properties/${propertyId}/similar`,
      {
        params: { limit },
      }
    );
    return response.data;
  },

  // Create new property (agent/admin only) (not implemented in backend yet)
  createProperty: async (
    propertyData: PropertyCreateData
  ): Promise<Property> => {
    const response = await apiClient.post("/api/properties", propertyData);
    return response.data;
  },

  // Update property (agent/admin only) (not implemented in backend yet)
  updateProperty: async (
    id: string,
    propertyData: PropertyUpdateData
  ): Promise<Property> => {
    const response = await apiClient.put(`/api/properties/${id}`, propertyData);
    return response.data;
  },

  // Delete property (agent/admin only) (not implemented in backend yet)
  deleteProperty: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/properties/${id}`);
  },

  // Upload property images (not implemented in backend yet)
  uploadImages: async (
    propertyId: string,
    images: File[]
  ): Promise<{ urls: string[] }> => {
    const formData = new FormData();
    images.forEach((image) => {
      formData.append("images", image);
    });

    const response = await apiClient.post(
      `/api/properties/${propertyId}/images`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Delete property image (not implemented in backend yet)
  deleteImage: async (propertyId: string, imageUrl: string): Promise<void> => {
    await apiClient.delete(`/api/properties/${propertyId}/images`, {
      data: { imageUrl },
    });
  },

  // Save property to favorites
  saveProperty: async (propertyId: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/api/properties/${propertyId}/save`);
    return response.data;
  },

  // Remove property from favorites
  unsaveProperty: async (propertyId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(
      `/api/properties/${propertyId}/save`
    );
    return response.data;
  },

  // Get saved properties
  getSavedProperties: async (): Promise<Property[]> => {
    const response = await apiClient.get("/api/properties/saved/list");
    // Backend returns { success, data }, frontend expects Property[] directly
    // Also map _id to id for frontend compatibility
    const properties = response.data.data.map((property: any) => ({
      ...property,
      id: property._id,
    }));
    return properties;
  },

  // Get property statistics (not implemented in backend yet)
  getPropertyStats: async (): Promise<any> => {
    const response = await apiClient.get("/api/properties/stats");
    return response.data;
  },

  // Get property views (not implemented in backend yet)
  getPropertyViews: async (
    propertyId: string
  ): Promise<{ views: number; uniqueViews: number }> => {
    const response = await apiClient.get(`/api/properties/${propertyId}/views`);
    return response.data;
  },

  // Increment property view (not implemented in backend yet)
  incrementView: async (propertyId: string): Promise<void> => {
    await apiClient.post(`/api/properties/${propertyId}/view`);
  },
};
